import type {
  AddClientRequestPayload,
  AddOtherPartyRequestPayload,
  AddStakeholderRequestPayload,
  AddVendorRequestPayload,
  StakeholderType
} from '@gat-ship/types'
import { globalUtils } from '@gat-ship/utils'

/**
 * Build the empty create-payload template for a stakeholder type. The type
 * controls which fields are present/required; a fresh `id` is generated so
 * the optimistic create + retry path keep a stable identity.
 */
export function buildInitialCreatePayload(
  stakeholderType: StakeholderType
): AddStakeholderRequestPayload {
  const id = globalUtils.functions.uuid()
  if (stakeholderType === 'client') {
    const payload: AddClientRequestPayload = {
      id,
      stakeholderType,
      name: '',
      shortName: '',
      email: undefined,
      phoneNumber: undefined,
      clientNumber: undefined,
      taxIdentificationNumber: undefined,
      eoriNumber: undefined,
      postalAddress: undefined,
      countryCode: undefined,
      visitAddress: undefined,
      visitAddressSameAsPostal: false,
      billingAddress: undefined,
      billingAddressSameAsPostal: true,
      paymentTermsPeriodInDays: undefined,
      paymentTermsUseRunningMonth: undefined,
      billingCurrencyCode: undefined,
      invoiceEmail: undefined,
      useDefaultPaymentTerms: true,
      useDefaultBillingCurrency: true,
      customFields: {},
      mainContacts: []
    }
    return payload as AddStakeholderRequestPayload
  }
  if (stakeholderType === 'vendor') {
    const payload: AddVendorRequestPayload = {
      stakeholderType,
      id,
      name: '',
      shortName: '',
      email: undefined,
      phoneNumber: undefined,
      vendorNumber: undefined,
      taxIdentificationNumber: undefined,
      eoriNumber: undefined,
      postalAddress: undefined,
      countryCode: undefined,
      visitAddress: undefined,
      visitAddressSameAsPostal: false,
      customFields: {},
      mainContacts: []
    }
    return payload as AddStakeholderRequestPayload
  }
  const payload: AddOtherPartyRequestPayload = {
    stakeholderType,
    id,
    name: '',
    shortName: '',
    email: undefined,
    phoneNumber: undefined,
    postalAddress: undefined,
    countryCode: undefined,
    visitAddress: undefined,
    visitAddressSameAsPostal: false,
    customFields: {},
    mainContacts: []
  }
  return payload as AddStakeholderRequestPayload
}

function isValidRequestPayload(
  requestPayload: AddStakeholderRequestPayload | undefined
): requestPayload is AddStakeholderRequestPayload {
  if (!requestPayload) {
    return false
  }
  return (
    requestPayload.name !== '' &&
    requestPayload.id !== '' &&
    requestPayload.shortName !== ''
  )
}

/** Clear client billing fields that the matching "use default" flag overrides. */
function clearDefaultedClientFields(
  result:
    | AddClientRequestPayload
    | AddVendorRequestPayload
    | AddOtherPartyRequestPayload,
  payload: AddStakeholderRequestPayload
) {
  if (payload.stakeholderType !== 'client') {
    return
  }
  if (
    (payload as AddClientRequestPayload).billingAddressSameAsPostal &&
    'billingAddress' in result
  ) {
    result.billingAddress = undefined
  }
  if (
    (payload as AddClientRequestPayload).useDefaultPaymentTerms &&
    'paymentTermsPeriodInDays' in result &&
    'paymentTermsUseRunningMonth' in result
  ) {
    result.paymentTermsPeriodInDays = undefined
    result.paymentTermsUseRunningMonth = undefined
  }
  if (
    (payload as AddClientRequestPayload).useDefaultBillingCurrency &&
    'billingCurrencyCode' in result
  ) {
    result.billingCurrencyCode = undefined
  }
}

/**
 * Validate the in-progress create payload and strip fields overridden by
 * "use default" / "same as postal" flags. Returns `undefined` when the
 * payload is incomplete.
 */
export function mapRequestPayloadToValidRequestPayload(
  payload: AddStakeholderRequestPayload | undefined
):
  | AddClientRequestPayload
  | AddVendorRequestPayload
  | AddOtherPartyRequestPayload
  | undefined {
  if (!payload || !isValidRequestPayload(payload)) {
    return undefined
  }
  const result:
    | AddClientRequestPayload
    | AddVendorRequestPayload
    | AddOtherPartyRequestPayload = payload
  clearDefaultedClientFields(result, payload)
  if (payload.visitAddressSameAsPostal) {
    result.visitAddress = undefined
  }

  return result
}
